// AI Asset GA4 bodies remain co-located with their wire-contract builders and validators.
import type { GA4AiAssetEngagementEvent, GA4AiAssetSearchEvent } from './aiAssetAnalyticsContract';

export interface TbidFields {
    company?: string;
    id?: string;
    orgId?: string;
    relationship?: string;
    role?: string;
}
export interface GtmTbid {
    tb: TbidFields;
}
export interface GtmPageLevelEvent {
    appCost: string;
    appType: string;
    loginStatus: string;
    userState: string;
    id: GtmTbid;
}

export interface GtmAnalyticsEvent {
    event: string;
    timestamp: number;
    eventContext: string;
}

export interface GtmSearchClickEvent extends GtmAnalyticsEvent {
    searchTerm: string;
    searchResultName: string;
    clickUrl: string;
    ctaClick: boolean;
    clickText: string;
    elementType: string;
    itemTitle?: string;
}
export interface GtmSearchEvent {
    event: string;
    searchResultsApps: number;
    searchResultsConsultants: number;
    searchTerm: string;
    searchResultName: string;
}
export interface GtmFilterSearchEvent {
    event: string;
    search_term: string;
    search_result_count: number;
    search_type: string;
}

export type GTMVersion = 'ga' | 'ga4';

export type GTMBaseEvent = {
    topic: string;
    elementType: string;
    timestamp: number;
    gtm?: GTMVersion;
    [key: string]: unknown;
};

// GA4 Interfaces
export interface GA4AnalyticsEvent {
    event: string;
}

export interface GA4PageLevelEvent {
    locale: string;
    login_status: string;
    page_path: string;
    page_path_with_query: string;
    page_referrer: string;
    referring_hostname: string;
    tbid_company: string;
    tbid_org_id: string;
    tbid_relationship: string;
    tbid_role: string;
    user_id: string;
    listing_url?: string;
    listing_id?: string;
    app_type?: string;
}

export interface GA4ListingDetailClickEvent extends GA4AnalyticsEvent {
    click_text: string;
    // Omitted (undefined) for non-navigational links — mailto: (support email PII)
    // and javascript: (modal-opening Terms & Conditions) — see GtmListingDetailClickType.
    click_url?: string;
    element_title: string;
    element_type: string;
    internal_driver: string;
    lp_detail_interaction: boolean;
    app_cost: string;
    app_type: string;
    featured: boolean;
    recommended: boolean;
    ignore_hover: boolean;
    listing_id: string;
    internal_article: boolean;
    listing_name: string;
    listing_type: string;
    listing_url: string;
    hover_intent: boolean;
    internal_source: string;
    page_type: string;
    // Selected install method (Claude Code / Cursor / Codex / OpenCode / Agentforce Vibes) for the install-card copy buttons.
    install_method?: string;
    // Package Files viewer: repo-relative path of the file whose view/copy triggered the click; absent on non-file clicks.
    file_path?: string;
}

export interface GA4ContentClickEvent extends GA4AnalyticsEvent {
    click_text?: string;
    click_url?: string;
    element_title?: string;
    element_type: string;
    content_category?: string;
    internal_driver?: string;
    listing_flow?: string;
    ctaClick?: boolean;
    // Which org-picker loader failed (loadTenants|loadFavorites|loadUserId); set only on the
    // ORG_PICKER_ERROR event so failures are filterable without overloading content_category.
    error_source?: string;
}

export interface GA4FormCompletionEvent extends GA4AnalyticsEvent {
    converting_page: string;
    internal_driver: string;
    form_environment_type: string;
    form_experience_type: string;
    form_join_id: string;
    form_name: string;
    form_primary_product_interest: string;
    hashed_email: string;
    offer_id: string;
    offer_type: string;
    partner_promo_code: string;
}
export interface GA4TileClickEvent extends GA4AnalyticsEvent {
    click_text: string;
    click_url: string;
    element_title: string;
    element_type: string;
    lp_detail_interaction: boolean;
    app_cost?: string;
    app_type?: string;
    featured: boolean;
    recommended: boolean;
    ignore_hover: boolean;
    listing_id: string;
    internal_article: boolean;
    listing_name: string;
    listing_type: string;
    listing_url: string;
    hover_intent: boolean;
    internal_source: string;
    page_type: string;
}
export interface GA4TryButtonClickEvent extends GA4AnalyticsEvent {
    click_text: string;
    ctaClick: boolean;
    click_url: string;
    element_title: string;
    element_type: string;
    content_category: string;
    listing_flow: string;
    internal_driver: string;
}
export interface GA4SearchResultClickEvent extends GA4TileClickEvent {
    search_term: string;
    search_type: string;
    search_category: string;
    search_result_clicks: number;
    app_results: number;
    consultant_results: number;
    agent_results: number;
    ai_summary_results?: number;
    slack_results?: number;
    salesforce_results?: number;
    position?: number;
}
export interface GA4TileViewEvent extends GA4AnalyticsEvent {
    event: string;
    element_title: string;
    element_type: string;
    lp_detail_interaction: boolean;
    app_cost?: string;
    app_type?: string;
    featured: boolean;
    recommended: boolean;
    ignore_hover: boolean;
    listing_id: string;
    internal_article: boolean;
    listing_name: string;
    listing_type: string;
    listing_url: string;
    hover_intent: boolean;
    internal_source: string;
    page_type: string;
}
export interface GA4SearchClickEvent extends GA4AnalyticsEvent {
    click_text: string;
    element_type: string;
    search_term: string;
    search_type: string;
    search_result_count?: number;
    search_category: string;
    app_results?: number;
    consultant_results?: number;
    agent_results?: number;
}

export interface GA4SearchEvent extends GA4AnalyticsEvent {
    search_term: string;
    search_type: string;
    search_result_count: number;
    search_category: string;
    app_results?: number;
    consultant_results?: number;
    agent_results?: number;
}

// Listing fields are optional: a related-search pill has no listing behind it.
export interface GA4AiContentImpressionEvent extends GA4AnalyticsEvent {
    element_name: string;
    element_title: string;
    element_type: string;
    lp_detail_interaction: boolean;
    featured: boolean;
    recommended: boolean;
    ignore_hover: boolean;
    internal_article: boolean;
    hover_intent: boolean;
    page_type: string;
    search_term: string;
    listing_id?: string;
    listing_name?: string;
    listing_type?: string;
    listing_url?: string;
    app_cost?: string;
    app_type?: string;
    internal_source?: string;
}

export interface GA4AiSearchEvent extends GA4SearchEvent {
    ai_summary_results: number;
    salesforce_results?: number;
    slack_results?: number;
    // 1-based rank of the related-search pill that initiated this search.
    position?: number;
}

export interface GA4FilterClickEvent extends GA4SearchClickEvent {
    filter_label: string;
    filter_action: string;
    app_results: number;
    consultant_results: number;
    agent_results: number;
}

export interface GA4FilterEngagementEvent extends GA4AnalyticsEvent {
    click_text: string;
    element_type: string;
    content_category?: string;
    filter_label: string;
    filter_action: string;
    search_term: string;
    search_type: string;
    search_result_count: number;
    search_results_count: number;
    search_category: string;
    app_results: number;
    agent_results: number;
    salesforce_results: number;
    slack_results: number;
    consultant_results: number;
    ai_summary_results: number;
}

export interface GA4LoginNavClickEvent extends GA4AnalyticsEvent {
    nav_level: string;
    nav_item: string;
    click_text: string;
    click_url: string;
    internal_driver: string;
    element_type: string;
    nav_type: string;
}

export interface GA4HeaderNavLinkClickEvent extends GA4AnalyticsEvent {
    nav_level: string;
    nav_item: string;
    click_text: string;
    click_url: string;
    internal_driver: string;
    element_type: string;
    nav_type: string;
}

export interface AuthenticationEvent extends GA4AnalyticsEvent {
    authentication_method: string;
    authentication_type: string;
}

export interface GA4CarouselClickEvent extends GA4AnalyticsEvent {
    click_text: string;
    click_url: string;
    element_type: string;
    element_title: string;
    lp_detail_interaction: boolean;
    app_cost: string;
    app_type: string;
    featured: boolean;
    recommended: boolean;
    ignore_hover: boolean;
    internal_article: boolean;
    internal_source: string;
    listing_id: string;
    listing_name: string;
    listing_type: string;
    listing_url: string;
    hover_intent: boolean;
    page_type: string;
}

export type GTMBaseEventWithTiles = {
    tiles: {
        name: string;
        title?: string;
    };
} & GTMBaseEvent;

export abstract class GtmEventType {
    abstract canHandle(event: GTMBaseEvent): boolean;
    abstract getBody(
        // @ts-expect-error * Automated Type Error Fix
        event,
    ):
        | GtmAnalyticsEvent
        | GA4AnalyticsEvent
        | GtmPageLevelEvent
        | GA4PageLevelEvent
        | GtmSearchEvent
        | GA4SearchEvent
        | GtmFilterSearchEvent
        | GA4ContentClickEvent
        | GA4FormCompletionEvent
        | GA4TileClickEvent
        | GA4HeaderNavLinkClickEvent
        | GA4CarouselClickEvent
        | GA4AiContentImpressionEvent
        | GA4AiSearchEvent
        | GA4AiAssetEngagementEvent
        | GA4AiAssetSearchEvent;

    // @ts-expect-error * Automated Type Error Fix
    isValidEvent(__event): boolean {
        return true;
    }

    // Handlers can expose bounded diagnostics without adding side effects to validators.
    // @ts-expect-error * Automated Type Error Fix
    getInvalidEventFields(__event): string[] {
        return [];
    }
}
